import { useBeschaffungswege } from '@/hooks/useBeschaffungswege';
import { SCHNELLWEGE, WEG_LAGER, WEG_VERLAG } from '@/lib/beschaffungWege';
import type { Beschaffungsweg } from '@/hooks/useBeschaffungswege';

// Die Schnellwege — Auf Lager, Libri, Umbreit, Verlag (A12.2, A19.1, D3).
//
// WARUM DIESES BAUTEIL ÜBERHAUPT EXISTIERT
//   Es gab die Knöpfe nur in der Detailansicht. Beim Erfassen einer
//   Bestellung fehlten sie, obwohl der Weg dort im selben Atemzug
//   feststeht wie die Position („das bestell ich bei Libri"). Der
//   Betreiber hat genau das gemeldet: „da fehlen die Quick-Buttons".
//
//   Abschreiben wäre der falsche Weg gewesen. Zwei Kopien laufen früher
//   oder später auseinander, und dann heißt derselbe Knopf an der einen
//   Stelle anders als an der anderen — oder er ordnet an der einen die
//   ganze Menge zu und an der anderen eine andere. Die Codes stehen
//   ohnehin schon an einer Stelle (lib/beschaffungWege.ts); die
//   Bedienung gehört daneben.
//
// WAS HIER NICHT STEHT
//   Die BESCHRIFTUNGEN. Die pflegt der Admin in der Tabelle
//   `beschaffungswege` (Designprinzip 2). Hier steht nur, WELCHE es
//   sind und in welcher Reihenfolge. Benennt der Admin „Libri" in „Libri
//   Barsortiment" um, heißt der Knopf mit.
//
//   Fehlt einer in der Tabelle — stillgelegt, umbenannt —,
//   fällt sein Knopf weg und die übrigen bleiben. Ein Knopf, dessen Weg
//   es nicht gibt, holte sich beim Antippen einen Fehler vom Server;
//   besser gar nicht erst da.
//
// WAS HIER AUCH NICHT STEHT
//   Was ein Tipp bewirkt. Die beiden Orte schreiben verschieden: die
//   Detailansicht legt einen Journaleintrag über den Server an, das
//   Erfassungsformular sammelt ihn im Entwurf und schickt ihn erst beim
//   Speichern mit. Das Bauteil meldet nur, WELCHER Weg getippt wurde und
//   für WIEVIEL — den Rest entscheidet, wer es einsetzt.

type Props = {
  /**
   * Wieviel noch offen ist. Bei 0 erscheinen gar keine Knöpfe (A19.3):
   * an einer fertig zugeordneten Position kosten sie Höhe ohne Ertrag.
   */
  offen: number;
  /** Ein Tipp: dieser Weg, diese Menge. */
  onWeg: (weg: Beschaffungsweg, menge: number) => void;
  /**
   * Anker für die Einführungstour, nur wo einer gebraucht wird.
   *
   * Bewusst nicht fest eingebaut: stünde er hier, trüge nach A19 jede
   * Positionskarte im Erfassungsformular denselben Anker wie die
   * Detailansicht. driver.js nimmt den ersten Treffer im Dokument — die
   * Tour zeigte dann irgendwann auf die falsche Stelle, ohne dass etwas
   * fehlschlüge.
   */
  tourAnker?: boolean;
};

export function SchnellwegKnoepfe({ offen, onWeg, tourAnker = false }: Props) {
  const wege = useBeschaffungswege();

  // Die Schnellwege, in der Reihenfolge aus SCHNELLWEGE und mit den
  // Beschriftungen aus der Tabelle. `filter` ist der Grund, warum ein
  // fehlender Weg nichts bricht: er fällt einfach heraus.
  const schnellwege = SCHNELLWEGE.map((code) =>
    (wege.data?.items ?? []).find((w) => w.code === code)
  ).filter((w): w is Beschaffungsweg => w !== undefined);

  if (offen <= 0 || schnellwege.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-ink-faint">{offen} offen — direkt zuordnen:</span>
      {schnellwege.map((w) => (
        <button
          key={w.id}
          type="button"
          data-nutzung="schnellweg"
          {...(tourAnker ? { 'data-tour': `schnellweg-${w.code}` } : {})}
          onClick={() => onWeg(w, offen)}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md border border-line bg-cream px-3 text-ink-soft hover:border-akzent hover:text-akzent"
          title={
            w.code === WEG_LAGER
              ? `Alle ${offen} aus dem eigenen Bestand — keine Bestellung`
              : w.code === WEG_VERLAG
                ? // D3: der Schnellweg erfasst Kanal und Verlagsname
                  // bewusst NICHT — beide sind freiwillig. Die
                  // Sprechblase sagt es, damit niemand sie sucht.
                  `Alle ${offen} beim Verlag zuordnen — ohne Kanal und Verlagsname (dafür: Formular)`
                : `Alle ${offen} bei ${w.label} zuordnen`
          }
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}
